import classNames from "classnames";
import { isNil } from "lodash";
import { CalendarMode, WorkforceMetricsMode } from "PFApp/booking/types";
import { LoadingDots } from "PFComponents/loading_dots";
import React from "react";
import { createPortal } from "react-dom";

import { useBookingsOverviewContext } from "../context/bookings_overview_context";
import CalendarRange from "./calendar_range";
import { MetricProgress } from "./metric_progress";
import { useMetricValue } from "./use_metric_value";
import css from "./workforce_metrics.module.scss";
import { WorkforceMetricsModeSelector } from "./workforce_metrics_mode_selector";

const METRIC_SUFFIXES = {
  [WorkforceMetricsMode.BillableUtilization]: "%",
  [WorkforceMetricsMode.TotalUtilization]: "%",
  [WorkforceMetricsMode.TotalAvailability]: "h"
};

interface WorkforceMetricsProps {
  headerRef: React.MutableRefObject<HTMLDivElement>;
  disabled: boolean;
}

export const WorkforceMetrics = ({ headerRef, disabled }: WorkforceMetricsProps): JSX.Element => {
  const {
    workforceMetricsMode,
    calendarMode,
    calendarData: { loaded: pageLoaded }
  } = useBookingsOverviewContext();

  const { metricValue, metricDisplayValue, isLoading } = useMetricValue();

  return createPortal(
    <div className={css.container}>
      {pageLoaded && isLoading && <LoadingDots circlesEnabled circleSize="xs" />}
      {!isNil(metricValue) && (
        <>
          <div className={css.metricContainer}>
            <div>
              <WorkforceMetricsModeSelector disabled={disabled} />
              <MetricProgress value={metricValue} />
            </div>
            <div className={classNames({ [css.disabled]: disabled })}>
              <span className={css.metricValue}>{metricDisplayValue}</span>
              {!!METRIC_SUFFIXES[workforceMetricsMode] && (
                <span className={css.suffix}>{METRIC_SUFFIXES[workforceMetricsMode]}</span>
              )}
            </div>
          </div>
          <div className={css.calendarContainer}>
            <CalendarRange
              disabled={isLoading || calendarMode !== CalendarMode.Workforce || disabled}
              tooltipDisabled={disabled}
            />
          </div>
        </>
      )}
    </div>,
    headerRef.current
  );
};
